import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import type {CommissionOrderType} from "@reva/shared/value-order";

import {selectSegmentBuildingTypeRentedIndicationPayload} from "../../redux/indicationSelectors";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import type {ApiQueryError} from "../../redux/types";
import {commissionValueOrder} from "../../redux/valueOrdersThunk";
import {buildApiQueryError, generateDefaultQueryError} from "../../util/errorUtil";
import type {AllowedCommissionOrderOptions} from "../../util/valueOrderEntitlements";
import {segmentBuildingTypeToPropertyType} from "../../util/valueOrderPropertyType";
import I18nErrorMessage from "../I18nErrorMessage";
import {ContactFields, ContactSection} from "./ContactPanels";
import {
    buildCommissionOrder,
    EMPTY_CONTACT,
    EMPTY_FORM,
    type FormChange,
    hasLegalInputs,
    shouldShowInvalid,
    validateCommissionOrder,
} from "./commissionOrderUtil";
import {ClientInfoPanel, DocumentTypePanels, OwnerInfoPanel, RegisterPanel} from "./DocumentCollectionPanels";
import {OrderTypeSection} from "./OrderTypeSection";

interface CommissionValueOrderDialogProps {
    visible: boolean;
    onClose?: () => void;
    allowedOrderTypes?: CommissionOrderType[] | null;
    missingOrderTypes?: CommissionOrderType[];
    allowedOrderOptions?: AllowedCommissionOrderOptions | null;
}

/**
 * Dialog zum Beauftragen einer Besichtigung bzw. Dokumentenbeschaffung bei der ValueAG.
 * @param visible Dialog sichtbar
 * @param onClose nach Abbruch oder erfolgreicher Beauftragung
 * @param allowedOrderTypes beim ValueAG-Auftraggeber freigeschaltete Auftragsarten; null/undefined = alle anbieten
 * @param missingOrderTypes nicht freigeschaltete Auftragsarten, die der Dialog als Hinweis nennt
 * @param allowedOrderOptions freigeschaltete Optionen je Auftragsart (`InteriorInspection`, `DocumentCollection`);
 *        fehlt eine Auftragsart, werden alle ihre Optionen angeboten
 */
export function CommissionValueOrderDialog({
    visible,
    onClose,
    allowedOrderTypes = null,
    missingOrderTypes = [],
    allowedOrderOptions = null,
}: CommissionValueOrderDialogProps) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();
    const segmentPayload = useRevaSelector(selectSegmentBuildingTypeRentedIndicationPayload);
    const propertyType = segmentBuildingTypeToPropertyType(segmentPayload?.segment, segmentPayload?.building_type);
    const [form, setForm] = useState(EMPTY_FORM);
    const [inProgress, setInProgress] = useState(false);
    const [commissionError, setCommissionError] = useState<ApiQueryError | null>(null);
    const [hoveringCommission, setHoveringCommission] = useState(false);
    const [touched, setTouched] = useState(false);

    const patch = (changes: FormChange) =>
        setForm((prev) => ({...prev, ...(typeof changes === "function" ? changes(prev) : changes)}));

    const {orderType, interiorOptions, documentOptions, creditorRegistered} = form;
    const isDocumentCollection = orderType === "DocumentCollection";

    // Treffen die Freischaltungen erst bei offenem Dialog ein, darf keine gesperrte Auftragsart gewählt bleiben.
    useEffect(() => {
        if (orderType && allowedOrderTypes && !allowedOrderTypes.includes(orderType)) {
            setForm((prev) => ({...prev, orderType: null, interiorOptions: [], documentOptions: []}));
        }
    }, [orderType, allowedOrderTypes]);

    // Ebenso keine Option, die beim Auftraggeber nicht freigeschaltet ist.
    const allowedInterior = allowedOrderOptions?.InteriorInspection;
    const allowedDocuments = allowedOrderOptions?.DocumentCollection;
    useEffect(() => {
        if (allowedInterior && interiorOptions.some((o) => !allowedInterior.includes(o))) {
            setForm((prev) => ({
                ...prev,
                interiorOptions: prev.interiorOptions.filter((o) => allowedInterior.includes(o)),
            }));
        }
        if (allowedDocuments && documentOptions.some((o) => !allowedDocuments.includes(o))) {
            setForm((prev) => ({
                ...prev,
                documentOptions: prev.documentOptions.filter((o) => allowedDocuments.includes(o)),
            }));
        }
    }, [allowedInterior, allowedDocuments, interiorOptions, documentOptions]);

    const isValid = validateCommissionOrder(form);
    const showInvalid = shouldShowInvalid({isValid, hovering: hoveringCommission, touched});

    const reset = () => {
        setForm(EMPTY_FORM());
        setCommissionError(null);
        setHoveringCommission(false);
        setTouched(false);
    };

    const handleCancel = () => {
        if (inProgress) return;
        reset();
        onClose?.();
    };

    const showLegalInputs = hasLegalInputs(orderType, documentOptions);

    const handleCommission = async () => {
        if (inProgress || !isValid || !orderType) return;
        setInProgress(true);
        setCommissionError(null);
        try {
            await dispatch(commissionValueOrder(buildCommissionOrder({...form, orderType}, propertyType)));
        } catch (e) {
            setInProgress(false);
            setCommissionError(buildApiQueryError(generateDefaultQueryError(e as Error)));
            return;
        }
        setInProgress(false);
        reset();
        onClose?.();
    };

    const footer = (
        <div>
            <Button
                className="p-button-text"
                label={t("general.cancel")}
                icon="pi pi-times"
                disabled={inProgress}
                onClick={handleCancel}
            />
            <span
                className="ml-2 inline-flex"
                onMouseEnter={() => setHoveringCommission(true)}
                onMouseLeave={() => setHoveringCommission(false)}
                // Touch kennt kein Hover: Antippen von „Beauftragen“ zeigt die fehlenden Pflichtfelder.
                onPointerDown={() => setTouched(true)}
            >
                <Button
                    label={t("commissionOrderDialog.commission")}
                    icon="pi pi-check"
                    disabled={inProgress || !isValid}
                    loading={inProgress}
                    onClick={() => void handleCommission()}
                />
            </span>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={handleCancel}
            footer={footer}
            header={t("commissionOrderDialog.title")}
            showCloseIcon={false}
            className="max-w-[51rem]"
        >
            <OrderTypeSection
                form={form}
                onChange={patch}
                allowedOrderTypes={allowedOrderTypes}
                missingOrderTypes={missingOrderTypes}
                allowedOrderOptions={allowedOrderOptions}
                disabled={inProgress}
                showInvalid={showInvalid}
            />

            {orderType === "InteriorInspection" && (
                <div className="commission-panel">
                    <div className="flex items-baseline gap-2 mb-4">
                        <span className="font-bold">{t("commissionOrderDialog.contact.main")}</span>
                        <span className="text-sm text-muted-color">{t("commissionOrderDialog.contact.mainHint")}</span>
                    </div>
                    <ContactFields
                        contact={form.mainContact}
                        onChange={(name, value) => patch((f) => ({mainContact: {...f.mainContact, [name]: value}}))}
                        disabled={inProgress}
                        idPrefix="main"
                        showInvalid={showInvalid}
                    />
                </div>
            )}
            {orderType && !isDocumentCollection && (
                <ContactSection
                    additionalContacts={form.additionalContacts}
                    onAdditionalContactChange={(index, name, value) =>
                        patch((f) => ({
                            additionalContacts: f.additionalContacts.map((c, i) =>
                                i === index ? {...c, [name]: value} : c,
                            ),
                        }))
                    }
                    onAddContact={() =>
                        patch((f) => ({additionalContacts: [...f.additionalContacts, EMPTY_CONTACT()]}))
                    }
                    onRemoveContact={(index) =>
                        patch((f) => ({additionalContacts: f.additionalContacts.filter((_, i) => i !== index)}))
                    }
                    disabled={inProgress}
                    showInvalid={showInvalid}
                />
            )}

            <DocumentTypePanels
                isDocumentCollection={isDocumentCollection}
                documentOptions={documentOptions}
                documentData={form.documentData}
                onChange={(key, field, value) =>
                    patch((f) => ({
                        documentData: {...f.documentData, [key]: {...f.documentData[key], [field]: value}},
                    }))
                }
                disabled={inProgress}
                showInvalid={showInvalid}
            />

            <RegisterPanel
                hasLegalInputs={showLegalInputs}
                registerData={form.registerData}
                disabled={inProgress}
                onChange={(field, value) => patch((f) => ({registerData: {...f.registerData, [field]: value}}))}
                showInvalid={showInvalid}
            />

            {showLegalInputs && (
                <OwnerInfoPanel
                    ownerType={form.ownerType}
                    onOwnerTypeChange={(ownerType) => patch({ownerType})}
                    ownerData={form.ownerData}
                    onChange={(field, value) => patch((f) => ({ownerData: {...f.ownerData, [field]: value}}))}
                    disabled={inProgress}
                    showInvalid={showInvalid}
                />
            )}

            {showLegalInputs && (
                <ClientInfoPanel
                    legitimateInterest={form.legitimateInterest}
                    onChange={(legitimateInterest) => patch({legitimateInterest})}
                    creditorRegistered={creditorRegistered}
                    onCreditorRegisteredChange={(value) =>
                        value !== creditorRegistered && patch({creditorRegistered: value, legalDocument: null})
                    }
                    legalDocument={form.legalDocument}
                    onLegalDocumentChange={(legalDocument) => patch({legalDocument})}
                    disabled={inProgress}
                    showInvalid={showInvalid}
                />
            )}

            <I18nErrorMessage
                titleI18nKey="commissionOrderDialog.error"
                errorObject={commissionError}
                className="mt-4"
            />
        </Dialog>
    );
}
